import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { WebBlePrintersState, WebBlePrintSettings } from './webBlePrinters.types';

// Modelo DINÁMICO de impresoras Web Bluetooth (BLE): un Record keyed por id de
// perfil, con altas/bajas. Espeja el slice de Electron (`electronPrintSettings`)
// para que los consumidores iteren `Object.keys(settings)` sin asumir keys fijas.
//
// Persistencia: blob JSON en localStorage bajo una llave DEDICADA
// (`webBlePrintersConfig`). Esa llave NO está en la allowlist de borrado del
// logout ni se toca en `resetStore` (el slice debe registrarse en
// DEVICE_SCOPED_SLICES del store), por lo que el perfil sobrevive login/logout
// del cajero — es propiedad del EQUIPO físico, no del usuario.
//
// A diferencia de Electron, NO hay migración de slots legacy: Web BLE es un
// camino nuevo. Un mapa vacío es un estado inicial válido (sin impresoras
// emparejadas todavía); el camino BLE es opt-in (decisión de arquitectura).

export type {
  WebBleAutoPrint,
  WebBlePageWidth,
  WebBlePrintersState,
  WebBlePrintSettings,
  WebBleTextSize,
  WebBleTicketType,
} from './webBlePrinters.types';

const CONFIG_KEY = 'webBlePrintersConfig';

const safeGet = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return localStorage.getItem(key);
  } catch {
    /* localStorage no disponible (tests/SSR) */
  }
  return null;
};

const safeSet = (key: string, value: string) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) localStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
};

export const makeWebBlePrinterId = (): string =>
  'wb' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const defaultWebBlePrintSettings = (patch: Partial<WebBlePrintSettings> = {}): WebBlePrintSettings => ({
  deviceId: null,
  deviceName: null,
  label: '',
  enabled: true,
  ticketFormat: 'text',
  showLogo: true,
  ticketType: 'normal',
  textSize: 'medium',
  // 58mm es el default SEGURO para BLE: las térmicas de bolsillo (PT-220 y similares) son de
  // 58mm. Un raster ("Como en la app") emitido a 80mm (576 dots) sobre un cabezal de 58mm
  // (384 dots) DESBORDA: la impresora vuelca los bytes sobrantes por fila como TEXTO (caracteres
  // acentuados/símbolos). El sentido inverso (58 en 80) sólo produce un ticket más angosto
  // (inofensivo). Por eso el default narrow. Ver migrateWidthDefault.
  pageWidth: 58,
  decoration: true,
  copies: 1,
  autoPrint: 'disabled',
  showClientPhone: true,
  showCommerceName: true,
  showCommerceAddress: true,
  ...patch,
});

const persist = (state: WebBlePrintersState) => safeSet(CONFIG_KEY, JSON.stringify(state));

// Marcador (llave dedicada) para correr la migración de ancho UNA sola vez.
const WIDTH_MIGRATION_KEY = 'webBlePrintersConfig:m:w58';

// Migración única del default histórico de `pageWidth`: antes era 80mm, un MAL default para BLE
// (las térmicas BLE son de bolsillo / 58mm; las de 80mm son de escritorio por USB/LAN → camino
// Electron, no BLE). Un raster "Como en la app" a 80mm sobre un cabezal de 58mm desborda y la
// impresora imprime los bytes sobrantes como texto corrupto (acentos/símbolos). Migramos los
// perfiles ya guardados 80→58 una sola vez; el marcador evita re-flip, así tras la migración el
// usuario puede elegir 80 a propósito (impresora de 80mm real) y se respeta. Es seguro por
// construcción: 58-en-80 sólo da un ticket más angosto, nunca basura.
export const migrateWidthDefault = (state: WebBlePrintersState): WebBlePrintersState => {
  if (safeGet(WIDTH_MIGRATION_KEY)) return state;
  let changed = false;
  const next: WebBlePrintersState = {};
  for (const [key, settings] of Object.entries(state)) {
    if (settings && settings.pageWidth === 80) {
      next[key] = { ...settings, pageWidth: 58 };
      changed = true;
    } else {
      next[key] = settings;
    }
  }
  safeSet(WIDTH_MIGRATION_KEY, '1');
  if (changed) persist(next);
  return next;
};

const initialState = (): WebBlePrintersState => {
  const blob = safeGet(CONFIG_KEY);
  if (blob) {
    try {
      const parsed = JSON.parse(blob) as WebBlePrintersState;
      if (parsed && typeof parsed === 'object') return migrateWidthDefault(parsed);
    } catch {
      /* blob corrupto → mapa vacío */
    }
  }
  // Sin perfiles guardados: marca la migración como hecha (no hay nada que migrar) para que un
  // perfil creado luego con el default nuevo (58) no se vuelva a tocar.
  safeSet(WIDTH_MIGRATION_KEY, '1');
  // Mapa vacío válido: el camino BLE es opt-in y arranca sin impresoras.
  return {};
};

export const webBlePrintersSlice = createSlice({
  name: 'webBlePrinters',
  initialState: initialState(),
  reducers: {
    updateWebBlePrintSettings: (
      state,
      action: PayloadAction<{ settings: Partial<WebBlePrintSettings>; printerKey: string }>,
    ) => {
      const { settings, printerKey } = action.payload;
      if (!state[printerKey]) return state;
      const next = { ...state, [printerKey]: { ...state[printerKey], ...settings } };
      persist(next);
      return next;
    },
    addPrinter: (state, action: PayloadAction<{ id?: string; patch?: Partial<WebBlePrintSettings> } | undefined>) => {
      const id = action.payload?.id || makeWebBlePrinterId();
      const next = { ...state, [id]: defaultWebBlePrintSettings(action.payload?.patch) };
      persist(next);
      return next;
    },
    removePrinter: (state, action: PayloadAction<{ printerKey: string }>) => {
      if (!state[action.payload.printerKey]) return state;
      const next = { ...state };
      delete next[action.payload.printerKey];
      persist(next);
      return next;
    },
    reset: () => {
      // El mapa vacío es el estado fresco. resetStore NO debe invocar esto (el
      // slice es device-scoped); existe por simetría con el resto de slices.
      const fresh: WebBlePrintersState = {};
      persist(fresh);
      return fresh;
    },
  },
});
